import i18n from '@/i18n'
import router from '@/router'
import { apiService } from '@/services'
import { HTTP_STATUS_BAD_REQUEST, HTTP_STATUS_OK, HTTP_STATUS_UNAUTHORIZED, SUPPORTED_LANGUAGES_TEMP } from '@/services/constants'
import { updateTitleLanguage } from '@/services/utils'
import { UserRolesNames, OrganizationTypes } from '@/services/organization-user/constants'
import store from '@/store'
import { deleteCookiesWithPrefix } from './utils'
import { clearReportsCache } from '@/components/reports/ReportsMenu'
import orgAuthLogin from '@/services/org-auth-login'
import { LoginUser } from '@/services/organization-user/types'
import { hideAndCloseMainAndOverlyModals } from '@/components/modals/modals-util'
import { AxiosError } from 'axios'
import { stopAllPolling } from '@/composables/useVideoPolling/useVideoPolling'

type StatusMessage = { status: number; message: string }

async function setUserData(userData: LoginUser): Promise<void> {
  store.commit('setAuthenticated', true)
  await store.dispatch('user/setUserOnboardingCompletedStep', userData.user.console_init_org_wizard_step)

  if (userData.roles[0].name === UserRolesNames.AgencyOwner) {
    store.commit('user/setIsRootUserAgency', true)
    store.commit('user/setMode', OrganizationTypes.Agency)
  }

  store.commit('todoList/setState', await apiService.organizationUser.getUserTodoList())

  const languageObj = SUPPORTED_LANGUAGES_TEMP.find((l) => l.id === userData.user.language_id)
  const languageCode = languageObj ? languageObj.code : 'en'
  i18n.global.locale.value = languageCode
  await store.dispatch('cache/setPageLanguage', languageCode)
  updateTitleLanguage(router.currentRoute.value.meta?.title)
}

async function executeAuthLogin(getUserData: () => Promise<LoginUser>): Promise<StatusMessage> {
  try {
    const userData = await getUserData()

    await store.dispatch('user/addNewUser', userData)
    await store.dispatch('user/extendOrgData')
    store.commit('user/setIsLoginFromAuth', true)

    await setUserData(userData)

    if (!userData.features) {
      userData.features = await apiService.features.getFeatureFlags()
      await store.dispatch('user/updateUserFeatures', userData.features)
    }

    return { status: HTTP_STATUS_OK, message: i18n.global.t('ok') }
  } catch (error) {
    return { status: HTTP_STATUS_BAD_REQUEST, message: i18n.global.t('login-error') }
  }
}

export default {
  async login(user: { email: string; password: string }): Promise<{ status: number; message: string }> {
    try {
      const userData = await apiService.organizationUser.login(user)
      // Check if the user already exists in the store
      let userIndex = store.state.user.users.findIndex((u) => u.user.email === user.email)
      if (userIndex === -1) {
        // User doesn't exist, so add them as a new user
        await store.dispatch('user/addNewUser', userData)
        userIndex = store.state.user.users.length - 1 // Index of the newly added user
      } else {
        // User exists, so update their token
        await store.dispatch('user/setAccessToken', { index: userIndex, userData })
      }

      await setUserData(userData)
      return { status: HTTP_STATUS_OK, message: i18n.global.t('ok') }
    } catch (error) {
      const err = error as AxiosError
      if (err.response?.status === HTTP_STATUS_UNAUTHORIZED) {
        return { status: HTTP_STATUS_UNAUTHORIZED, message: i18n.global.t('login-error') }
      } else {
        return { status: HTTP_STATUS_BAD_REQUEST, message: `${err.message} - ${i18n.global.t('dashboard-contact-us-part-two')}` }
      }
    }
  },

  async loginFromAuth(organizationId: number, userId: number, token: string) {
    return executeAuthLogin(() => orgAuthLogin(Number(organizationId), Number(userId), token))
  },

  async loginFromSSO(code: string) {
    return executeAuthLogin(() => apiService.organization.authenticateOrganizationWithCode(code))
  },

  async setUserData(userData: LoginUser): Promise<void> {
    await setUserData(userData)
  },

  // async switchToRemoteAccount(id): Promise<{  status: number, message: string}> {
  async switchToRemoteAccount(orgId: number, userId: number): Promise<{ status: number; message: string }> {
    try {
      // Check if the user already exists in the store
      let userIndex = store.state.user.users.findIndex((u) => u.user.id === userId)

      if (userIndex === -1) {
        // User doesn't exist, so fetch their data from the API
        // const userData = await apiService.login(user);
        const userData = await apiService.organization.loginRemoteAccount(orgId, userId)
        if (userData) {
          await store.dispatch('user/addNewUser', userData)
          await store.dispatch('user/extendOrgData')
        }
        // Add the new user to the store
        userIndex = store.state.user.users.length - 1 // Index of the newly added user
      }

      // Set the active user to be the user who just logged in
      await store.dispatch('user/setActiveUser', userIndex)
      store.commit('setAuthenticated', true)

      store.commit('todoList/setState', await apiService.organizationUser.getUserTodoList())
      //Set user mode, we need this to handle UI
      store.commit('user/setMode', 'user')

      router.push('/')

      return { status: 200, message: 'OK' }
    } catch (error) {
      return { status: 400, message: 'Error' }
    }
  },

  async getUserInfo(userId: number) {
    try {
      const userInfo = await apiService.organizationUser.getUserInfo(userId)
      store.commit('user/setUserInfo', userInfo)
      return userInfo
    } catch (error) {
      return { status: 400, message: 'The email or password is incorrect' }
    }
  },

  async logout(routeToLogin = true) {
    stopAllPolling()
    if (routeToLogin) {
      await router.push('/login')
    }
    store.dispatch('cache/setIsUserLoggedIn', false)
    store.commit('setAuthenticated', false)
    // store.commit('setRedirectRoute', '')
    store.commit('user/logout')
    store.commit('folders/logout')
    store.commit('domains/logout')
    store.commit('reports/logout')
    store.commit('dashboard/logout')
    store.commit('library/logout')
    store.commit('packages/logout')
    store.commit('playlists/resetPlaylistState')
    store.commit('settings/logout')
    store.commit('widget/unsetWidget')
    store.commit('widgets/logout')
    store.commit('cache/clearCachedRoles')
    store.commit('todoList/logout')
    store.dispatch('domains/reset')
    hideAndCloseMainAndOverlyModals()
    clearReportsCache()
    deleteCookiesWithPrefix('infobar')
    if (import.meta.env.VITE_APP_IS_PRODUCTION === 'true') {
      heap.resetIdentity()
      hj('identify', null)
      mixpanel.reset()
    }
    // store.commit('cache/logout')
  }
}
